import React, { useCallback, useEffect, useState } from 'react';
import { useApiCaller } from '../../utils/ApiUtilities';
import { useError } from '../ErrorContext';
import { getAdminPaymentsUrl, getAdminPaymentRefundUrl } from '../../utils/UrlUtilities';
import { constructPaymentRefundPayload } from '../../utils/PayloadUtils';
import { itemsPerPageOptions } from '../../utils/Constants';
import { isEmpty } from '../../utils/BasicUtilities';

const TYPE_OPTIONS = [
    { value: 'PREMIUM_PURCHASE', label: 'Purchase' },
    { value: 'REFERRAL_REWARD', label: 'Referral reward' },
    { value: 'REFUND', label: 'Refund' },
    { value: 'AWARD', label: 'Award' },
];

const STATUS_BADGE = {
    SUCCESS: 'bg-success',
    PENDING: 'bg-warning text-dark',
    FAILED: 'bg-danger',
    REFUNDED: 'bg-secondary',
};

export default function PaymentsDashboard() {
    const CallApi = useApiCaller();
    const { showSuccess } = useError();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState({ q: '', transaction_type: '', status: '', date_from: '', date_to: '' });
    const [applied, setApplied] = useState({ q: '', transaction_type: '', status: '', date_from: '', date_to: '' });

    // Refund modal.
    const [refundFor, setRefundFor] = useState(null); // txn row
    const [refundForm, setRefundForm] = useState({ amount: '', phone: '', reason: '', cancel: false });
    const [refunding, setRefunding] = useState(false);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        const params = { page, limit };
        Object.entries(applied).forEach(([k, v]) => { if (!isEmpty(v)) params[k] = v; });
        const res = await CallApi({ url: getAdminPaymentsUrl(params), method: 'GET', token: true });
        if (res.success) {
            setRows(res.data.transactions || []);
            setTotal(res.data.total || 0);
        }
        setLoading(false);
    }, [page, limit, applied, CallApi]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    const handleRefund = async () => {
        if (!refundFor) return;
        setRefunding(true);
        const res = await CallApi({
            url: getAdminPaymentRefundUrl(refundFor.id),
            method: 'POST',
            payload: constructPaymentRefundPayload({
                amountBdt: refundForm.amount,
                recipientPhone: refundForm.phone,
                reason: refundForm.reason,
                cancelSubscription: refundForm.cancel,
            }),
            token: true,
        });
        setRefunding(false);
        if (res.success) {
            showSuccess(res.data?.status === 'SUCCESS' ? 'Refund issued.' : `Refund ${res.data?.status}.`);
            setRefundFor(null);
            setRefundForm({ amount: '', phone: '', reason: '', cancel: false });
            fetchRows();
        }
    };

    const totalPages = Math.ceil(total / limit) || 1;

    return (
        <div className="container py-4">
            <h2 className="fw-bold mb-4" style={{ color: '#4c1d95' }}>Payments Dashboard</h2>

            {/* Filters */}
            <div className="card mb-3 kp-filter"><div className="card-body row g-2 align-items-end">
                <div className="col-6 col-md-3">
                    <label className="form-label small mb-1">Search user</label>
                    <input className="form-control form-control-sm" value={pending.q} onChange={(e) => setPending({ ...pending, q: e.target.value })} placeholder="name / email" />
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">Type</label>
                    <select className="form-select form-select-sm" value={pending.transaction_type} onChange={(e) => setPending({ ...pending, transaction_type: e.target.value })}>
                        <option value="">All</option>
                        {TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">Status</label>
                    <select className="form-select form-select-sm" value={pending.status} onChange={(e) => setPending({ ...pending, status: e.target.value })}>
                        <option value="">All</option>
                        <option value="SUCCESS">Success</option>
                        <option value="PENDING">Pending</option>
                        <option value="FAILED">Failed</option>
                        <option value="REFUNDED">Refunded</option>
                    </select>
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">From</label>
                    <input type="date" className="form-control form-control-sm" value={pending.date_from} onChange={(e) => setPending({ ...pending, date_from: e.target.value })} />
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">To</label>
                    <input type="date" className="form-control form-control-sm" value={pending.date_to} onChange={(e) => setPending({ ...pending, date_to: e.target.value })} />
                </div>
                <div className="col-6 col-md-1">
                    <button className="btn btn-sm btn-outline-primary w-100" onClick={() => { setApplied(pending); setPage(1); }}>Filter</button>
                </div>
            </div></div>

            {/* Grid */}
            <div className="card"><div className="card-body p-0 table-responsive">
                <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            <th>User</th><th>Type</th><th>Amount</th><th>Status</th><th>Ref / Account</th><th>Date</th><th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="text-center text-muted py-4">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={7} className="text-center text-muted py-4">No transactions found.</td></tr>
                        ) : rows.map((t) => (
                            <tr key={t.id}>
                                <td>
                                    <div className="fw-semibold">{t.user_name || '—'}</div>
                                    <div className="small text-muted">{t.user_email}</div>
                                </td>
                                <td className="small">{(TYPE_OPTIONS.find((o) => o.value === t.transaction_type) || {}).label || t.transaction_type}</td>
                                <td>৳{t.amount_bdt}</td>
                                <td><span className={`badge ${STATUS_BADGE[t.status] || 'bg-secondary'}`}>{t.status}</span></td>
                                <td className="small text-muted">
                                    {t.gateway_ref || '—'}
                                    {t.masked_account ? <div>{t.masked_account}</div> : null}
                                    {t.refunded_txn_id ? <div className="text-secondary">↩ refund #{t.refunded_txn_id}</div> : null}
                                </td>
                                <td className="small text-muted">{new Date(t.created_at).toLocaleDateString()}</td>
                                <td className="text-end">
                                    {t.transaction_type === 'PREMIUM_PURCHASE' && t.status === 'SUCCESS' ? (
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => { setRefundFor(t); setRefundForm({ amount: String(t.amount_bdt || ''), phone: '', reason: '', cancel: false }); }}>
                                            Refund
                                        </button>
                                    ) : <span className="text-muted small">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div></div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-2">
                    <label className="small mb-0">Items per page:</label>
                    <select className="form-select form-select-sm w-auto" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
                        {itemsPerPageOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                    <span className="small text-muted">Page {page} of {totalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
                </div>
            </div>

            {/* Refund modal */}
            {refundFor && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060 }}>
                    <div className="card" style={{ width: '90%', maxWidth: 'min(480px, 94vw)' }}>
                        <div className="card-header fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>Refund purchase</div>
                        <div className="card-body">
                            <p className="small mb-2">
                                Refund <strong>{refundFor.user_name}</strong> &lt;{refundFor.user_email}&gt; for purchase #{refundFor.id} (৳{refundFor.amount_bdt}).
                            </p>
                            <div className="row g-2">
                                <div className="col-6">
                                    <label className="form-label small mb-1">Amount (৳)</label>
                                    <input type="number" min="1" max={refundFor.amount_bdt} className="form-control form-control-sm" value={refundForm.amount} onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} />
                                    <div className="form-text small">Partial refunds allowed (max ৳{refundFor.amount_bdt}).</div>
                                </div>
                                <div className="col-6">
                                    <label className="form-label small mb-1">bKash number</label>
                                    <input className="form-control form-control-sm" placeholder="defaults to user phone" value={refundForm.phone} onChange={(e) => setRefundForm({ ...refundForm, phone: e.target.value })} />
                                </div>
                                <div className="col-12">
                                    <label className="form-label small mb-1">Reason</label>
                                    <input className="form-control form-control-sm" value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} placeholder="e.g. duplicate charge" />
                                </div>
                            </div>
                            <div className="form-check mt-2">
                                <input className="form-check-input" type="checkbox" id="cancelSubToggle" checked={refundForm.cancel} onChange={(e) => setRefundForm({ ...refundForm, cancel: e.target.checked })} />
                                <label className="form-check-label small" htmlFor="cancelSubToggle">Also cancel the user's current subscription</label>
                            </div>
                            <p className="small text-danger mt-2 mb-0">This records a bKash payout and marks the purchase refunded.</p>
                        </div>
                        <div className="card-footer d-flex justify-content-end gap-2">
                            <button className="btn btn-outline-secondary" onClick={() => setRefundFor(null)} disabled={refunding}>Cancel</button>
                            <button className="btn btn-danger" onClick={handleRefund} disabled={refunding || !(Number(refundForm.amount) > 0)}>{refunding ? 'Refunding…' : 'Issue refund'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
